import { Timestamp } from "firebase/firestore";
import { UserData } from "../store/actions/authActions";

// Function to get specific part of the state from localStorage
export const loadStateFromLocalStorage = <T>(key: string): T | undefined => {
  try {
    const serializedState = localStorage.getItem(key);
    if (serializedState === null) {
      return undefined; // Return undefined to use the default initial state
    }
    return JSON.parse(serializedState) as T; // Parse and return the stored state
  } catch (err) {
    console.error(`Error loading state from localStorage for key ${key}`, err);
    return undefined;
  }
};

// Function to save specific part of the state to localStorage
export const saveStateToLocalStorage = <T>(key: string, state: T) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(key, serializedState);
  } catch (err) {
    console.error(`Error saving state to localStorage for key ${key}`, err);
  }
};

export const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

export const capitalizeFirstLetter = (val: string) => {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
};

export const addSevenDays = (dateString: string) => {
  // Parse the input date string
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a new Date object
  const date = new Date(year, month - 1, day);

  // Add 7 days
  date.setDate(date.getDate() + 7);

  // Format the new date as `${yyyy}-${mm}-${dd}`
  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, "0");
  const newDay = String(date.getDate()).padStart(2, "0");

  return `${newYear}-${newMonth}-${newDay}`;
};

export const getCurrentDateFormatted = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// export const jsDate = (timestamp: Timestamp) => timestamp.toDate();

// You can now use `loadStateFromLocalStorage` and `saveStateToLocalStorage` in any reducer or slice.

//import { Timestamp } from 'firebase/firestore'; // or 'firebase-admin/firestore' if you're using admin SDK

export function jsDate(
  timestamp:
    | Timestamp
    | { _seconds: number; _nanoseconds: number }
    | null
    | undefined
): Date | null {
  if (!timestamp) return null;

  // Firestore Timestamp instance
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }

  // Plain object with _seconds and _nanoseconds
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "_seconds" in timestamp &&
    typeof timestamp._seconds === "number"
  ) {
    return new Date(timestamp._seconds * 1000);
  }

  return null;
}

export function formatHumanReadableDate(
  input: string | Date,
  locale: string = "en-US"
): string {
  const date = new Date(input);

  if (isNaN(date.getTime())) return "Invalid date";

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",

  };

  return new Intl.DateTimeFormat(locale, options).format(date);
}
export function formatDate(
  dateInput: string | Date | number | undefined
): string {
  if (!dateInput) return "Never";

  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "Never";

  return new Intl.DateTimeFormat("en-UK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}
export function normalizeBusinessName(input: string): string {
  // Special case override
  const cleaned = input.trim().toLowerCase();
  if (cleaned === "sopher") return "Sopher Private Office";
  if (cleaned === "sopher-test") return "Sopher Private Office Test";

  // Replace underscores, dashes, and multiple spaces with a single space
  const normalized = input
    .replace(/[_\-]+/g, " ") // underscores and dashes to space
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to space-separated
    .replace(/[^a-zA-Z ]+/g, "") // remove non-letters except spaces
    .replace(/\s+/g, " ") // multiple spaces to one
    .trim();

  // Capitalize each word
  return normalized
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export const getBusinessNamesForAdmin = (user: UserData) => {
  const isAdmin = user.roles.includes("master");
  let businessNames: string[];

  switch (user.business?.name) {
    case "sopher":
      businessNames = isAdmin ? ["sopher", "SOPHER_ACCOUNTING"] : ["sopher"];
      break;

    case "sopher-test": {
      businessNames = isAdmin
        ? ["sopher-test", "SOPHER_ACCOUNTING_TEST"]
        : ["sopher-test"];
      break;
    }

    default: {
      businessNames = user.business ? [user.business.name] : [];
      break;
    }
  }
  return businessNames;
};

/**
 * Returns a date string in 'YYYY-MM-DD' format
 * that is `days` days from today.
 *
 * @param days Number of days to add to current date
 * @returns Formatted date string (e.g., "2025-12-14")
 */
export function getFutureDate(days: number): string {
  const date = new Date();
  console.log("Current date:", date);
  date.setDate(date.getDate() + days);
  console.log(`Future date after ${days} days:`, date);
  // Extract year, month, and day in local time
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  console.log(`Formatted date: ${year}-${month}-${day}`);
  return `${year}-${month}-${day}`;
}

//whole string to uppercase
export function uppercase(str: string): string {
  return str.toUpperCase();
}


export function parseDateMessage(input: string): { date: string; message: string } {
  // Match a date pattern that stops right before the first non-date character
  const match = input.match(
    /^([0-9]{4}[-/][0-9]{2}[-/][0-9]{2}(?:[T ]?[0-9:.+-Z]*)?)-(.*)$/
  );

  if (!match) {
    return { date: input.trim(), message: "" };
  }

  const [, date, message] = match;
  return { date: date.trim(), message: message.trim() };
}


/**
 * Convert a File to a base64 string with data URI prefix
 * @param file - The File object to convert
 * @returns Promise<string> - Base64 string with format: data:{mimeType};base64,{base64}
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result); // Already includes data:image/png;base64, prefix
      } else {
        reject(new Error('Failed to convert file to base64: result is not a string'));
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsDataURL(file);
  });
};

export function processClientCode(clientCode: string): string {
  // Remove the last segment after the last '_' or '-' and remove all remaining '_'
  return clientCode
    .replace(/[-_][^-_]*$/, '') // Remove the last segment after the last '-' or '_'
    .replace(/_/g, ''); // Remove all remaining '_'
}


/**
 * Convert a theme object to a readable JavaScript object string
 * with proper indentation and no quotes around keys
 */
export const themeToJSObjectString = (theme: any): string => {
  if (!theme) return '';
  
  const formatValue = (value: any, indent: number = 2): string => {
    const spaces = ' '.repeat(indent);
    
    if (typeof value === 'string') {
      return `"${value}"`;
    }
    
    if (Array.isArray(value)) {
      const items = value.map(item => formatValue(item, indent + 2)).join(',\n' + spaces);
      return `[\n${spaces}${items}\n${' '.repeat(indent - 2)}]`;
    }
    
    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value)
        .map(([key, val]) => `${spaces}${key}: ${formatValue(val, indent + 2)}`)
        .join(',\n');
      return `{\n${entries}\n${' '.repeat(indent - 2)}}`;
    }
    
    return String(value);
  };
  
  return formatValue(theme, 2);
};

